/**
 * @fileoverview Presentation mapping for per-event "why" reason chips on the
 * Discover list rows (FRE-910).
 *
 * The wire contract ({@link WhyReason} from `@loci/types`) carries the
 * server-rendered `displayText` plus a stable `code`. Copy is rendered
 * server-side and shown verbatim; the client only keys an icon + accent theme
 * off the `code` so the chips stay visually consistent across surfaces without
 * re-deriving meaning from the prose.
 *
 * Accent split mirrors the DS v1.5.0 `discover.html` `.disc-event__why`
 * treatment, where personal / urgency reasons take the warm ember tint
 * (`--clt-ember-light`) and the rest take the themed accent
 * (`--accent`; WEB-650 / DPL-40):
 *   - `FOLLOW_ARTIST` / `FOLLOW_VENUE` → ember (a relationship the viewer opted
 *     into — the warmest signal).
 *   - `TIME_OF_DAY` → ember (temporal urgency — "late-night vibe", "tonight").
 *   - `CATEGORY_MATCH` / `GENRE_SIMILARITY` / `PROXIMITY` → skyline (taste +
 *     spatial alignment).
 *
 * Icons are lucide PascalCase keys consumed by the `Icon` primitive.
 *
 * @author LOCI Web Team
 * @since FRE-910 (Discover · per-event why-reason tags + capacity/walk-time)
 */

import { type icons } from 'lucide-react';
import type { WhyReasonCode } from '@loci/types';

/** Visual accent applied to a reason chip — drives icon + text token color. */
export type WhyReasonAccent = 'skyline' | 'ember';

/**
 * WEB-673 / DPL-23 — the pill's SEMANTIC family: what kind of signal this is,
 * independent of its color. `taste` (a preference/relationship match), `geo`
 * (spatial proximity), or `time` (temporal fit). Exposed via `data-variant`
 * so the ≤720 rail and the RTL suite can key off "what kind of pill is this"
 * without re-deriving it from `code`.
 *
 * Deliberately a SEPARATE dimension from {@link WhyReasonAccent}: the issue
 * explicitly preserves FRE-910's existing ember/skyline accent-per-code
 * mapping unchanged, so `variant` never drives color — only classification.
 *
 * WEB-1078 — rows show positive signals only. The retired dashed
 * "No taste signal yet" chip is gone for every viewer (the list tail states
 * the absence once). The dashed neutral `.dv-sig--none` treatment is reserved
 * for FACTUAL attributes (`21+`, `Hands-on · 12 seats`, `No start time
 * published`) and never for a reason, so it is not a member of this union.
 */
export type WhyReasonVariant = 'taste' | 'geo' | 'time';

/** Presentation descriptor for a single reason chip, keyed by `code`. */
export interface WhyReasonChipPresentation {
  /** Lucide icon (decorative — the `displayText` carries the meaning for AT). */
  readonly iconName: keyof typeof icons;
  /** Accent theme — `ember` for personal/urgent codes, `skyline` otherwise. FRE-910, unchanged by WEB-673. */
  readonly accent: WhyReasonAccent;
  /** Pill family — `taste` / `geo` / `time` (WEB-673). */
  readonly variant: WhyReasonVariant;
}

/**
 * Map every `WhyReasonCode` to its chip presentation. A `Record` (not a
 * `switch` with a default) so adding a future code is a compile error here
 * rather than a silent fallback — the whole closed set must be themed.
 */
const WHY_REASON_PRESENTATION: Record<WhyReasonCode, WhyReasonChipPresentation> = {
  FOLLOW_ARTIST: { iconName: 'Heart', accent: 'ember', variant: 'taste' },
  FOLLOW_VENUE: { iconName: 'Heart', accent: 'ember', variant: 'taste' },
  TIME_OF_DAY: { iconName: 'Clock', accent: 'ember', variant: 'time' },
  CATEGORY_MATCH: { iconName: 'Sparkles', accent: 'skyline', variant: 'taste' },
  GENRE_SIMILARITY: { iconName: 'Music', accent: 'skyline', variant: 'taste' },
  PROXIMITY: { iconName: 'Navigation', accent: 'skyline', variant: 'geo' },
};

/**
 * Resolve the icon + accent + variant for a reason `code`. Defensive fallback
 * to a neutral skyline `Sparkles` `taste` pill keeps the chip rendering even
 * if the wire ever carries an out-of-contract code (forward-compat with a
 * server that adds a code before the client ships the mapping).
 */
export function getWhyReasonChipPresentation(code: WhyReasonCode): WhyReasonChipPresentation {
  return (
    WHY_REASON_PRESENTATION[code] ?? { iconName: 'Sparkles', accent: 'skyline', variant: 'taste' }
  );
}
